import {
  getFormattedDateTime,
  adjustFromTimeZoneSafely,
  adjustToTimeZoneSafely,
  Restriction,
  RestrictionScheduleWindow,
  restrictionTypes,
  usStateCodes,
} from '@ots-frontend/ots-data';
import type { OtsBadgeTone } from '../components/OtsBadge';
import {
  fromScheduleWindowDrafts,
  OtsScheduleWindowDraft,
  toScheduleWindowDrafts,
  validateScheduleWindows,
} from '../components/ots-schedule';
import type { OtsSelectOption } from '../components/OtsSelect';

/** What a restriction applies to; the server allows exactly one per restriction. */
export type RestrictionScope = 'OBJECT' | 'COUNTY_STATE' | 'BOUNDARY';
export type RestrictionStatus = 'ACTIVE' | 'SCHEDULED' | 'ENDED';

export const RESTRICTION_VIEW_PERMISSION = 'restriction_view';
export const RESTRICTION_CREATE_PERMISSION = 'restriction_create';
export const RESTRICTION_EDIT_PERMISSION = 'restriction_edit';
export const RESTRICTION_DELETE_PERMISSION = 'restriction_delete';

export const RESTRICTION_SCOPE_LABELS: Record<RestrictionScope, string> = {
  OBJECT: 'Object',
  COUNTY_STATE: 'County/State',
  BOUNDARY: 'Boundary',
};

/** Restriction object types; `entityType` is the key the entity selects load by. */
export const RESTRICTION_OBJECT_TYPES: Array<{
  value: string;
  label: string;
  entityType: string;
}> = [
  { value: 'SITE', label: 'Site', entityType: 'SITE' },
  { value: 'ROUTE', label: 'Route', entityType: 'ROUTE' },
  { value: 'WELL', label: 'Well', entityType: 'WELL' },
  {
    value: 'CONCOMMIGLED',
    label: 'Container cluster',
    entityType: 'CONTAINER_CLUSTER',
  },
  { value: 'CONTAINER', label: 'Container', entityType: 'CONTAINER' },
];

export const restrictionTypeOptions: OtsSelectOption[] = restrictionTypes.map(
  (type) => ({
    value: type.code,
    label: type.displayValue.defaultMessage as string,
  })
);

export const usStateOptions: OtsSelectOption[] = usStateCodes.map((state) => ({
  value: state.code,
  label: state.code,
}));

export const restrictionTypeLabel = (code?: string) =>
  restrictionTypeOptions.find((option) => option.value === code)?.label ??
  code ??
  'Unknown';

export const restrictionTypeTone = (code?: string): OtsBadgeTone => {
  switch (code) {
    case 'HUNTING':
      return 'warning';
    case 'WEATHER':
      return 'blue';
    case 'ROAD':
      return 'neutral';
    case 'REJECTION':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const objectTypeLabel = (code?: string) =>
  RESTRICTION_OBJECT_TYPES.find((type) => type.value === code)?.label ?? code;

export const objectTypeEntityType = (code?: string) =>
  RESTRICTION_OBJECT_TYPES.find((type) => type.value === code)?.entityType ??
  code;

export const hasBoundary = (restriction?: Pick<Restriction, 'boundary'>) =>
  (restriction?.boundary?.features?.length ?? 0) > 0;

export const restrictionScope = (
  restriction?: Partial<Restriction>
): RestrictionScope => {
  if (!restriction) return 'OBJECT';
  if (hasBoundary(restriction)) return 'BOUNDARY';
  if (restriction.county || restriction.state) return 'COUNTY_STATE';
  if (!restriction.objectId && restriction.companyId) return 'COUNTY_STATE';
  return 'OBJECT';
};

/** Saved restriction times are wall-clock values; convert them to a browser instant. */
export const toLocalTime = (dttm?: number) =>
  adjustFromTimeZoneSafely(dttm)?.getTime();

/** Converts a browser instant back to the wall-clock value the server stores. */
export const toStoredTime = (local?: number) =>
  adjustToTimeZoneSafely(local)?.getTime();

/** Formats a saved restriction time with the common region-aware display formatter. */
export const formatRestrictionDttm = (dttm?: number, fallback = '—') =>
  dttm == null ? fallback : getFormattedDateTime(dttm);

export const restrictionStatus = (
  restriction: Pick<Restriction, 'startDttm' | 'endDttm'>,
  now: number = Date.now()
): RestrictionStatus => {
  const start = toLocalTime(restriction.startDttm);
  const end = toLocalTime(restriction.endDttm);
  if (start != null && start > now) return 'SCHEDULED';
  if (end != null && end <= now) return 'ENDED';
  return 'ACTIVE';
};

export const RESTRICTION_STATUS_LABELS: Record<RestrictionStatus, string> = {
  ACTIVE: 'Active',
  SCHEDULED: 'Scheduled',
  ENDED: 'Ended',
};

export const RESTRICTION_STATUS_TONES: Record<RestrictionStatus, OtsBadgeTone> =
  {
    ACTIVE: 'success',
    SCHEDULED: 'info',
    ENDED: 'neutral',
  };

export const restrictionName = (restriction: Partial<Restriction>) =>
  restriction.displayName?.trim() || restrictionTypeLabel(restriction.type);

export const restrictionTargetLabel = (restriction: Partial<Restriction>) => {
  const scope = restrictionScope(restriction);
  if (scope === 'OBJECT') {
    if (restriction.objectName) return restriction.objectName;
    if (restriction.objectId)
      return `${objectTypeLabel(restriction.objectType) ?? 'Object'} ${
        restriction.objectId
      }`;
    return 'Regional';
  }
  if (scope === 'BOUNDARY') return 'Drawn boundary';
  return [restriction.county, restriction.state].filter(Boolean).join(', ');
};

export const restrictionScopeDescription = (
  restriction: Partial<Restriction>
) => {
  const scope = restrictionScope(restriction);
  if (scope === 'OBJECT')
    return objectTypeLabel(restriction.objectType) ?? 'Object';
  const company = restriction.companyName ?? restriction.companyId;
  return company
    ? `${RESTRICTION_SCOPE_LABELS[scope]} · ${company}`
    : RESTRICTION_SCOPE_LABELS[scope];
};

export type RestrictionFormValue = {
  scope: RestrictionScope;
  type?: string;
  displayName: string;
  description: string;
  objectType?: string;
  objectId?: string;
  companyId?: string;
  state?: string;
  county: string;
  boundary: string;
  /** Browser instants for the date pickers; converted with `toStoredTime` on save. */
  startDttm?: number;
  endDttm?: number;
  applyScheduleWindows: boolean;
  scheduleWindows: OtsScheduleWindowDraft[];
};

export type RestrictionFormErrors = Partial<
  Record<
    | 'type'
    | 'objectType'
    | 'objectId'
    | 'companyId'
    | 'location'
    | 'state'
    | 'boundary'
    | 'endDttm'
    | 'scheduleWindows',
    string
  >
>;

export const toRestrictionFormValue = (
  restriction?: Partial<Restriction>
): RestrictionFormValue => ({
  scope: restrictionScope(restriction),
  type: restriction?.type,
  displayName: restriction?.displayName ?? '',
  description: restriction?.description ?? '',
  objectType: restriction?.objectType,
  objectId: restriction?.objectId,
  companyId: restriction?.companyId,
  state: restriction?.state,
  county: restriction?.county ?? '',
  boundary: hasBoundary(restriction)
    ? JSON.stringify(restriction?.boundary, null, 2)
    : '',
  startDttm: toLocalTime(restriction?.startDttm),
  endDttm: toLocalTime(restriction?.endDttm),
  applyScheduleWindows: restriction?.applyScheduleWindows ?? true,
  scheduleWindows: toScheduleWindowDrafts(restriction?.scheduleWindows),
});

const isPolygon = (geometry?: { type?: string }) =>
  geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon';

/** Accepts a FeatureCollection, Feature, or bare polygon and returns a FeatureCollection. */
export const parseBoundary = (text: string): GeoJSON.FeatureCollection => {
  if (!text.trim()) throw new Error('Draw or paste a boundary.');
  let value: GeoJSON.GeoJSON;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error('Boundary must be valid GeoJSON.');
  }
  if (value.type === 'FeatureCollection') {
    if (!value.features.length) throw new Error('Draw or paste a boundary.');
    if (!value.features.every((feature) => isPolygon(feature.geometry)))
      throw new Error('Boundary features must be polygons.');
    return value;
  }
  if (value.type === 'Feature') {
    if (!isPolygon(value.geometry ?? undefined))
      throw new Error('Boundary must be a polygon.');
    return {
      type: 'FeatureCollection',
      features: [{ ...value, properties: value.properties ?? {} }],
    };
  }
  if (isPolygon(value)) {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: value as GeoJSON.Polygon,
        },
      ],
    };
  }
  throw new Error('Boundary must be a polygon.');
};

export const validateRestrictionForm = (
  value: RestrictionFormValue
): RestrictionFormErrors => {
  const errors: RestrictionFormErrors = {};
  if (!value.type) errors.type = 'Select a restriction type.';
  if (value.scope === 'OBJECT') {
    if (!value.objectType) errors.objectType = 'Select an object type.';
    else if (!value.objectId) errors.objectId = 'Select an object.';
  } else if (!value.companyId) {
    errors.companyId = 'Select a company.';
  }
  if (value.scope === 'COUNTY_STATE') {
    if (!value.state && !value.county.trim())
      errors.location = 'Enter a county or state.';
    else if (value.county.trim() && !value.state)
      errors.state = 'State is required when county is set.';
  }
  if (value.scope === 'BOUNDARY') {
    try {
      parseBoundary(value.boundary);
    } catch (error) {
      errors.boundary = (error as Error).message;
    }
  }
  if (
    value.startDttm != null &&
    value.endDttm != null &&
    value.endDttm <= value.startDttm
  )
    errors.endDttm = 'End must be after start.';
  const windowError = validateScheduleWindows(value.scheduleWindows);
  if (windowError) errors.scheduleWindows = windowError;
  return errors;
};

/**
 * Builds the request body. Fields outside the chosen scope are cleared so an edit that
 * changes scope never leaves the server with two scopes.
 */
export const toRestrictionPayload = (
  value: RestrictionFormValue,
  existing?: Partial<Restriction>
): Partial<Restriction> => {
  const objectScope = value.scope === 'OBJECT';
  const countyScope = value.scope === 'COUNTY_STATE';
  const boundaryScope = value.scope === 'BOUNDARY';
  return {
    ...(existing?.id ? { id: existing.id } : {}),
    type: value.type,
    displayName: value.displayName.trim() || undefined,
    description: value.description.trim() || undefined,
    objectType: objectScope ? value.objectType : undefined,
    objectId: objectScope ? value.objectId : undefined,
    companyId: objectScope ? undefined : value.companyId,
    state: countyScope ? value.state : undefined,
    county: countyScope ? value.county.trim() || undefined : undefined,
    boundary: boundaryScope ? parseBoundary(value.boundary) : undefined,
    startDttm: toStoredTime(value.startDttm),
    endDttm: toStoredTime(value.endDttm),
    applyScheduleWindows: value.applyScheduleWindows,
    scheduleWindows: fromScheduleWindowDrafts(value.scheduleWindows),
  };
};

/** Copies keep the scope, schedule, and notes but start as a new, unsaved restriction. */
export const copyRestriction = (
  restriction: Partial<Restriction>
): Partial<Restriction> => {
  const { id, createDttm, lastModifiedDttm, createById, ...rest } = restriction;
  void id;
  void createDttm;
  void lastModifiedDttm;
  void createById;
  return {
    ...rest,
    displayName: rest.displayName ? `${rest.displayName} (copy)` : undefined,
  };
};

export const restrictionSearchText = (restriction: Partial<Restriction>) =>
  [
    restriction.displayName,
    restrictionTypeLabel(restriction.type),
    restrictionTargetLabel(restriction),
    restriction.companyName,
    restriction.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
